import { useEffect } from 'react';
import { DURATION_TIME_UNITS } from '@/main/app.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  BaseToolPersistedState,
  ToolConfigParams,
  ToolConfigValue,
  ToolRehydratePayload,
} from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export type PeriodicConditionState = BaseToolPersistedState & {
  /** Timezone used to interpret calendar boundaries. */
  timezone: string | undefined;
  /** Selected calendar or custom period operator. */
  duration: string | undefined;
  /** Selected days, months, or quarters, in selection order. */
  frequencies: string[];
  /** First day of a calendar week. */
  weekStart: string;
  /** First month and day of a fiscal quarter. */
  quarterStart: { month: string; day: number | string };
  /** Start hour and length of a shift. */
  shift: { offset: number | string; duration: number | string };
  /** Offset applied to generated capsules. */
  offset: ValueWithUnitsItem | undefined;
  /** Length of each custom capsule. */
  customDuration: ValueWithUnitsItem | undefined;
  /** Spacing between custom capsules. */
  customPeriod: ValueWithUnitsItem | undefined;
  /** Whether custom period follows custom duration. */
  customPeriodDisable: boolean;
};

export type PeriodicConditionDehydratedState = Pick<
  PeriodicConditionState,
  | 'id'
  | 'name'
  | 'originalParameters'
  | 'maximumDuration'
  | 'advancedParametersCollapsed'
  | 'configVersion'
  | 'timezone'
  | 'duration'
  | 'frequencies'
  | 'weekStart'
  | 'quarterStart'
  | 'shift'
  | 'offset'
  | 'customDuration'
  | 'customPeriod'
  | 'customPeriodDisable'
>;

export type PeriodicConditionConfigInput = Omit<PeriodicConditionState, 'id' | 'name' | 'originalParameters'>;

type ValueWithUnitsInput = { value?: number | string; units?: string } | undefined;

type PeriodicConditionActions = {
  /** Changing duration clears the selected frequencies. */
  setDuration: (duration: string) => void;
  setTimezone: (timezone: string | undefined) => void;
  setWeekStart: (weekStart: string) => void;
  setQuarterStart: (month: string, day: number | string) => void;
  setShift: (offset: number | string, duration: number | string) => void;
  /** Passing no frequency clears all selected frequencies. */
  toggleFrequency: (frequency?: string) => void;
  /** Clears incomplete or non-numeric offsets instead of storing partial form input. */
  setOffset: (offset: ValueWithUnitsInput) => void;
  /** Clears incomplete or non-numeric periods instead of storing partial form input. */
  setCustomPeriod: (customPeriod: ValueWithUnitsInput) => void;
  /** Keeps the disabled custom period aligned when its previous value matches the duration. */
  setCustomDuration: (customDuration: ValueWithUnitsInput) => void;
  /** Toggles whether the custom period follows the custom duration. */
  toggleCustomPeriod: () => void;
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsedState: (collapsed: boolean) => void;
  /** Defaults an edited config without a timezone to the user's current timezone. */
  rehydrateForEdit: (payload: ToolRehydratePayload, defaultTimezone: string | undefined) => void;
  reset: ResettableStoreState<PeriodicConditionState>;
};

const initialState: PeriodicConditionState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  timezone: undefined,
  duration: undefined,
  frequencies: [],
  weekStart: 'Day.Sunday',
  quarterStart: { month: 'Month.January', day: 1 },
  shift: { offset: 8, duration: 8 },
  offset: { value: 0, units: DURATION_TIME_UNITS[1].unit[0] },
  customDuration: { value: 0, units: DURATION_TIME_UNITS[2].unit[0] },
  customPeriod: { value: 0, units: DURATION_TIME_UNITS[2].unit[0] },
  customPeriodDisable: true,
});

const normalizeValueWithUnits = (value: ValueWithUnitsInput): ValueWithUnitsItem | undefined => {
  if (value?.value == null || value.units == null || Number.isNaN(Number(value.value))) {
    return undefined;
  }
  return { value: Number(value.value), units: value.units };
};

export const dehydratePeriodicConditionState = (state: PeriodicConditionState): PeriodicConditionDehydratedState => ({
  id: state.id,
  name: state.name,
  originalParameters: state.originalParameters,
  maximumDuration: state.maximumDuration,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  configVersion: state.configVersion,
  timezone: state.timezone,
  duration: state.duration,
  frequencies: state.frequencies,
  weekStart: state.weekStart,
  quarterStart: state.quarterStart,
  shift: state.shift,
  offset: state.offset,
  customDuration: state.customDuration,
  customPeriod: state.customPeriod,
  customPeriodDisable: state.customPeriodDisable,
});

export function getPeriodicConditionConfigParams(state: PeriodicConditionConfigInput): ToolConfigParams {
  return getToolConfigParams({
    serializedState: { ...state } as Record<string, ToolConfigValue>,
    toolType: TREND_TOOLS.PERIODIC_CONDITION,
    parameterDefinitionKeys: [],
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  });
}

const periodicConditionActionsFactory: ActionsFactory<PeriodicConditionState, PeriodicConditionActions> = (
  set,
  _get,
  resetState,
) => ({
  setDuration: (duration) =>
    set((draft) => {
      if (draft.duration !== duration) draft.frequencies = [];
      draft.duration = duration;
    }),
  setTimezone: (timezone) =>
    set((draft) => {
      draft.timezone = timezone;
    }),
  setWeekStart: (weekStart) =>
    set((draft) => {
      draft.weekStart = weekStart;
    }),
  setQuarterStart: (month, day) =>
    set((draft) => {
      draft.quarterStart = { month, day };
    }),
  setShift: (offset, duration) =>
    set((draft) => {
      draft.shift = { offset, duration };
    }),
  toggleFrequency: (frequency) =>
    set((draft) => {
      if (!frequency) {
        draft.frequencies = [];
      } else {
        const index = draft.frequencies.indexOf(frequency);
        if (index >= 0) draft.frequencies.splice(index, 1);
        else draft.frequencies.push(frequency);
      }
    }),
  setOffset: (offset) =>
    set((draft) => {
      draft.offset = normalizeValueWithUnits(offset);
    }),
  setCustomPeriod: (customPeriod) =>
    set((draft) => {
      draft.customPeriod = normalizeValueWithUnits(customPeriod);
    }),
  setCustomDuration: (customDuration) =>
    set((draft) => {
      const normalized = normalizeValueWithUnits(customDuration);
      if (
        draft.customPeriodDisable &&
        draft.customDuration?.value === draft.customPeriod?.value &&
        draft.customDuration?.units === draft.customPeriod?.units
      ) {
        draft.customPeriod = normalized;
      }
      draft.customDuration = normalized;
    }),
  toggleCustomPeriod: () =>
    set((draft) => {
      draft.customPeriodDisable = !draft.customPeriodDisable;
    }),
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),
  setAdvancedParametersCollapsedState: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),
  rehydrateForEdit: (payload, defaultTimezone) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, { timezone: defaultTimezone }, rehydrationState.config, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),
  reset: resetState,
});

const STORE_NAME = 'PeriodicCondition';
const [PeriodicConditionStoreProvider, usePeriodicConditionStoreInstance, usePeriodicConditionStoreSelector] =
  createStore(STORE_NAME, initialState, periodicConditionActionsFactory);

export { PeriodicConditionStoreProvider };

export const usePeriodicConditionId = () => usePeriodicConditionStoreSelector((state) => state.id);
export const usePeriodicConditionName = () => usePeriodicConditionStoreSelector((state) => state.name);
export const usePeriodicConditionOriginalParameters = () =>
  usePeriodicConditionStoreSelector((state) => state.originalParameters);
export const usePeriodicConditionShift = () => usePeriodicConditionStoreSelector((state) => state.shift);
export const usePeriodicConditionDuration = () => usePeriodicConditionStoreSelector((state) => state.duration);
export const usePeriodicConditionFrequencies = () => usePeriodicConditionStoreSelector((state) => state.frequencies);
export const usePeriodicConditionOffset = () => usePeriodicConditionStoreSelector((state) => state.offset);
export const usePeriodicConditionQuarterStart = () => usePeriodicConditionStoreSelector((state) => state.quarterStart);
export const usePeriodicConditionTimezone = () => usePeriodicConditionStoreSelector((state) => state.timezone);
export const usePeriodicConditionWeekStart = () => usePeriodicConditionStoreSelector((state) => state.weekStart);
export const usePeriodicConditionCustomPeriod = () => usePeriodicConditionStoreSelector((state) => state.customPeriod);
export const usePeriodicConditionCustomDuration = () =>
  usePeriodicConditionStoreSelector((state) => state.customDuration);
export const usePeriodicConditionCustomPeriodDisable = () =>
  usePeriodicConditionStoreSelector((state) => state.customPeriodDisable);
export const usePeriodicConditionAdvancedParametersCollapsed = () =>
  usePeriodicConditionStoreSelector((state) => state.advancedParametersCollapsed);
export const usePeriodicConditionMaximumDuration = () =>
  usePeriodicConditionStoreSelector((state) => state.maximumDuration);
export const usePeriodicConditionConfigVersion = () =>
  usePeriodicConditionStoreSelector((state) => state.configVersion);
export const usePeriodicConditionActions = () => usePeriodicConditionStoreInstance().actions;

/** @deprecated Only for the legacy singleton bridge during Flux migration. */
export function legacyCreatePeriodicConditionStore() {
  return createStoreInstance(initialState, periodicConditionActionsFactory, { storeName: STORE_NAME });
}

export const usePersistPeriodicConditionStore = () => {
  const register = useRegisterPersistedStore();
  const store = usePeriodicConditionStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqPeriodicConditionStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydratePeriodicConditionState,
      }),
    [register, store],
  );
};
